import { oc } from "@orpc/contract";
import {
  QueryCatalogSchema,
  QueryIRSchema,
  QueryResultColumnSchema,
} from "@erp/shared-types";
import { z } from "zod";

/**
 * User-driven query builder over inventory + procurement.
 *
 * `catalog` returns the public field catalog (display metadata only — no
 * physical schema) that drives the field picker. `run` accepts a query IR,
 * validates it against the backend registry, compiles it to parameterized SQL,
 * and returns the result rows + column metadata.
 *
 * See docs/projectplans/inventory-procurement-query-builder.md.
 */

const QueryRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

export const RunQueryResponseSchema = z.object({
  rows: z.array(QueryRowSchema),
  columns: z.array(QueryResultColumnSchema),
  /** "detail" = row-level; "summary" = grouped/aggregated. */
  mode: z.enum(["detail", "summary"]),
  /** True if the row cap (limit) was hit, so more rows may exist. */
  truncated: z.boolean(),
  /**
   * Total rows matching the query's filters, ignoring limit/offset (groups in
   * summary mode). Drives pagination and "exporting N rows".
   */
  totalCount: z.number().int().nonnegative(),
});

export type RunQueryResponse = z.infer<typeof RunQueryResponseSchema>;

export const SuggestValuesInputSchema = z.object({
  /** Subject area key the field belongs to. */
  base: z.string(),
  /** Field key to suggest distinct values for (string/enum fields only). */
  field: z.string(),
  /** Optional case-insensitive substring the user is typing. */
  search: z.string().optional(),
  limit: z.number().int().min(1).max(50).optional().default(20),
});

export const SuggestValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export type SuggestValuesInput = z.input<typeof SuggestValuesInputSchema>;
export type SuggestValuesResponse = z.infer<typeof SuggestValuesResponseSchema>;

export const PreviewQueryResponseSchema = z.object({
  /** The parameterized SQL the engine would execute. */
  sql: z.string(),
  /** Same SQL with bind values substituted in — display only, never run. */
  inlinedSql: z.string(),
  /** Ordered bind parameters backing the parameterized SQL. */
  params: z.array(z.object({ name: z.string(), value: z.string() })),
});
export type PreviewQueryResponse = z.infer<typeof PreviewQueryResponseSchema>;

export const queryBuilderContracts = {
  catalog: oc.input(z.object({})).output(QueryCatalogSchema).route({
    method: "GET",
    summary: "Get the inventory query builder field catalog",
    description:
      "Returns the subject areas and fields (with display metadata) available in the inventory and procurement query builder's field picker.",
  }),
  run: oc.input(QueryIRSchema).output(RunQueryResponseSchema).route({
    method: "POST",
    summary: "Run an inventory query builder query",
    description:
      "Validates and runs a query-builder query over live inventory and procurement data; returns rows, columns, detail or summary mode, total count and truncation flag.",
  }),
  suggest: oc
    .input(SuggestValuesInputSchema)
    .output(SuggestValuesResponseSchema)
    .route({
      method: "GET",
      summary: "Suggest values for a query builder field",
      description:
        "Type-ahead: returns up to 50 distinct values for a text or enum field in a query-builder subject area, filtered by a search substring.",
    }),
  preview: oc.input(QueryIRSchema).output(PreviewQueryResponseSchema).route({
    method: "POST",
    summary: "Preview SQL for a query builder query",
    description:
      "Compiles a query-builder query to its parameterized SQL, an inlined display version and bind parameters, without running it.",
  }),
} as const;
